import { Link } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import { ChipRow, ChipToggle } from "@/components/dashboard/resources-filters/filter-controls";
import { ReportError } from "@/components/reports/hours/ReportError";
import { ReportFreshness } from "@/components/reports/hours/ReportFreshness";
import { ReportRangePicker } from "@/components/reports/hours/ReportRangePicker";
import {
  BASIS_LABEL,
  formatHrs,
  formatMonthColumn,
  pct,
} from "@/components/reports/hours/report-format";
import { EmptyState } from "@/components/shared/empty-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type DemandByAccountReport,
  type DemandMonthCell,
  toQueryString,
  useDemandByAccountReport,
} from "@/hooks/use-hours-reports";
import { type DemandSearch, demandApiParams } from "@/lib/report-search";
import { cn } from "@/lib/utils";

/** Guidance on top; Actuals and Guidance % beneath for past and current months. */
function MonthCell({ cell, strong = false }: { cell: DemandMonthCell; strong?: boolean }) {
  const forecast = cell.basis === "forecast";
  const title = [
    BASIS_LABEL[cell.basis],
    `Guidance ${cell.guidanceHours === null ? "–" : `${formatHrs(cell.guidanceHours)} hrs`}`,
    forecast
      ? null
      : `Actuals ${cell.actualHours === null ? "–" : `${formatHrs(cell.actualHours)} hrs`}`,
    forecast || cell.deltaHours === null ? null : `Delta ${formatHrs(cell.deltaHours)} hrs`,
    forecast
      ? null
      : `Guidance % ${pct(cell.guidancePct)}${cell.basis === "inProgress" ? " (MTD)" : ""}`,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <td
      title={title}
      className={cn(
        "border-l border-border/60 px-2 py-1.5 align-top font-mono text-xs tabular-nums",
        forecast && "report-forecast",
        strong && "font-semibold",
      )}
    >
      <div className="text-foreground">
        {cell.guidanceHours === null ? "–" : formatHrs(cell.guidanceHours)}
      </div>
      <div className="whitespace-nowrap text-muted-foreground">
        {forecast
          ? "Forecast"
          : cell.actualHours === null
            ? "–"
            : `${formatHrs(cell.actualHours)} · ${pct(cell.guidancePct)}`}
      </div>
    </td>
  );
}

function DemandTable({ report }: { report: DemandByAccountReport }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full min-w-[960px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left">
            <th scope="col" className="sticky left-0 z-10 w-56 bg-muted px-3 py-2 font-medium">
              Account
            </th>
            {report.months.map((m, i) => (
              <th
                key={m.month}
                scope="col"
                title={BASIS_LABEL[m.basis]}
                className={cn(
                  "border-l border-border/60 px-2 py-2 font-medium",
                  m.basis === "forecast" && "report-forecast",
                  m.basis === "inProgress" && "bg-brand-50",
                )}
              >
                <div className="whitespace-nowrap">{formatMonthColumn(m.month, i === 0)}</div>
                <div className="text-xs font-normal text-muted-foreground">
                  {m.basis === "forecast"
                    ? "Forecast"
                    : m.basis === "inProgress"
                      ? "MTD"
                      : "Actual"}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.rows.map((row) => (
            <tr key={row.accountId} className="border-b border-border/60 hover:bg-muted/30">
              <th
                scope="row"
                className="sticky left-0 z-10 bg-card px-3 py-1.5 text-left font-normal"
              >
                <Link
                  to="/dashboard/demands"
                  search={{ q: row.accountName }}
                  title={`Open ${row.accountName}'s Demands and who is guided on them`}
                  className="block max-w-52 truncate font-medium text-foreground hover:text-primary hover:underline"
                >
                  {row.accountName}
                </Link>
                <span className="block text-xs text-muted-foreground">
                  {formatHrs(row.guidanceHoursInRange)} hrs guided · up to {row.peakDemandCount}{" "}
                  Demand
                  {row.peakDemandCount === 1 ? "" : "s"}
                </span>
              </th>
              {row.months.map((c) => (
                <MonthCell key={c.month} cell={c} />
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-border bg-muted/40">
            <th
              scope="row"
              className="sticky left-0 z-10 bg-muted px-3 py-2 text-left text-sm font-semibold"
            >
              Total
            </th>
            {report.total.map((c) => (
              <MonthCell key={c.month} cell={c} strong />
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * Reports → Demand by Account (spec E2.1): Guidance hours on Active Demands by Account
 * and month, with Actuals and Guidance % where the month has started. Account-scoped.
 */
export function DemandByAccountPage({
  search,
  setSearch,
}: {
  search: DemandSearch;
  setSearch: (next: DemandSearch) => void;
}) {
  const qs = toQueryString(demandApiParams(search));
  const { data, isLoading, isError, error, refetch, isFetching } = useDemandByAccountReport(qs);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Reports"
        title="Demand by Account"
        description="Where delivery hours are guided, and how much of it is being logged."
      />
      <div className="flex flex-wrap items-center gap-3">
        {data && (
          <ReportRangePicker
            range={data.range}
            onChange={(r) => setSearch({ ...search, from: r.from, to: r.to })}
          />
        )}
        <ChipRow>
          <ChipToggle
            active={search.internal === "include"}
            onToggle={() =>
              setSearch({
                ...search,
                internal: search.internal === "include" ? undefined : "include",
              })
            }
          >
            Include internal Demands
          </ChipToggle>
        </ChipRow>
        {data && <ReportFreshness dataAsOf={data.dataAsOf} />}
      </div>
      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : isError ? (
        <ReportError error={error} onRetry={() => refetch()} />
      ) : data && data.rows.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No guided hours in this range"
          description={
            data.scoped
              ? "None of your Accounts has Guidance or logged hours on an Active Demand in this range."
              : "No Account has Guidance or logged hours on an Active Demand in this range."
          }
        />
      ) : data ? (
        <div className={cn("flex flex-col gap-2", isFetching && "opacity-70")}>
          <DemandTable report={data} />
          <p className="text-xs text-muted-foreground">
            Top figure: Guidance hrs. Beneath: Actuals hrs · Guidance % (Actuals ÷ Guidance), month
            to date for the current month. Active Demands only.
            {data.scoped ? " Showing the Accounts you own." : ""}
          </p>
        </div>
      ) : null}
    </PageContainer>
  );
}
